/**
 * TurnstileChallenge — the widget half of BK-153-1.
 *
 * Reports one of three outcomes upward and never blocks the form itself:
 *   solved      → token available, send it
 *   unavailable → script/widget failed; caller FAILS OPEN (see lib/turnstile.ts)
 *   expired     → token went stale; caller must get a fresh one before submit
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  getTurnstileSiteKey,
  loadTurnstile,
  turnstileEnabled,
  type TurnstileStatus,
} from "@/lib/turnstile";

export type TurnstileChallengeHandle = {
  /** Clears the current token and asks for a fresh challenge. */
  reset: () => void;
};

export default function TurnstileChallenge({
  onToken,
  onStatusChange,
  resetSignal = 0,
}: {
  onToken: (token: string | null) => void;
  onStatusChange?: (status: TurnstileStatus) => void;
  /** Increment to force a fresh challenge (a token is single-use). */
  resetSignal?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [status, setStatus] = useState<TurnstileStatus>(
    turnstileEnabled() ? "loading" : "disabled",
  );

  const report = useCallback(
    (next: TurnstileStatus) => {
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange],
  );

  useEffect(() => {
    if (!turnstileEnabled()) {
      report("disabled");
      onToken(null);
      return;
    }

    let cancelled = false;

    void loadTurnstile().then((turnstile) => {
      if (cancelled) return;
      if (!turnstile || !containerRef.current) {
        // Script blocked, offline, or timed out: fail open and say so.
        report("unavailable");
        onToken(null);
        return;
      }

      try {
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: getTurnstileSiteKey(),
          theme: "dark",
          callback: (token: string) => {
            report("solved");
            onToken(token);
          },
          "error-callback": () => {
            // A widget error is still an unreachable challenge → fail open.
            report("unavailable");
            onToken(null);
          },
          "expired-callback": () => {
            report("expired");
            onToken(null);
          },
        });
        report("ready");
      } catch {
        report("unavailable");
        onToken(null);
      }
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Removal is best-effort; the node unmounts regardless.
        }
      }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resetSignal) return;
    onToken(null);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        report("ready");
      } catch {
        report("unavailable");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  if (status === "disabled" || status === "unavailable") {
    // Nothing to show: the flow continues unchallenged by design.
    return null;
  }

  return (
    <div className="mt-4">
      <div ref={containerRef} data-testid="turnstile-widget" />
      {status === "expired" ? (
        <p className="mt-2 text-xs text-ios-secondary">
          The human check expired. Complete it again to continue.
        </p>
      ) : null}
    </div>
  );
}
